import { useRef, useState } from "react";
import { Plus, Paperclip, Trash2, CheckCircle2, X, FileText, ChevronDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import NumberInput from "@/components/app/NumberInput";
import DateInput from "@/components/app/DateInput";
import { fmtIDR, fmtDate } from "@/lib_app/api";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);

/** Payment ledger of a single invoice (Phase 3 — AR). */
export default function InvoicePaymentsPanel({ grandTotal = 0, paid = 0, payments = [], canEdit = false, locked = false, lockedReason = "", onAdd, onDelete, onOpenAttachment }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pay, setPay] = useState({ amount: 0, paid_at: today(), method: "", note: "" });
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);

  const outstanding = Math.max(0, grandTotal - paid);
  const pct = grandTotal > 0 ? Math.min(100, paid * 100 / grandTotal) : 0;

  const pickFile = (f) => {
    if (!f) { setFile(null); return; }
    const ext = (f.name.split(".").pop() || "").toLowerCase();
    if (!["pdf", "png", "jpg", "jpeg", "webp", "doc", "docx", "xls", "xlsx"].includes(ext)) return toast.error("Ekstensi tidak diizinkan");
    if (f.size > 15 * 1024 * 1024) return toast.error("File > 15MB");
    setFile(f);
  };

  const submit = async () => {
    if (!pay.amount || pay.amount <= 0) return toast.error("Nominal harus > 0");
    if (!pay.note?.trim()) return toast.error("Keterangan pembayaran wajib diisi");
    setBusy(true);
    try {
      await onAdd?.(pay, file);
      setPay({ amount: 0, paid_at: today(), method: "", note: "" });
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      setOpen(false);
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="invoice-payments-panel">
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-2xl bg-[#E4F9EC] border border-[#B7E7C7] p-4">
          <div className="text-[9px] uppercase tracking-widest text-[#4A6D53] font-bold">Terbayar</div>
          <div className="text-lg font-bold text-[#3EB876] mt-0.5 font-num tabular-nums" data-testid="invoice-paid-amount">{fmtIDR(paid)}</div>
          <div className="text-[10px] text-[#4A6D53] mt-0.5 font-num tabular-nums">{pct.toFixed(2)}% dari tagihan</div>
        </div>
        <div className="rounded-2xl bg-[#FDE7E9] border border-[#FFB3BF] p-4">
          <div className="text-[9px] uppercase tracking-widest text-[#8E4747] font-bold">Sisa Tagihan</div>
          <div className="text-lg font-bold text-[#CE6968] mt-0.5 font-num tabular-nums" data-testid="invoice-outstanding">{fmtIDR(outstanding)}</div>
          <div className="text-[10px] text-[#8E4747] mt-0.5 font-num tabular-nums">{Math.max(0, 100 - pct).toFixed(2)}% sisa</div>
        </div>
        <div className="rounded-2xl bg-[#E5EEFA] border border-[#B0C7EA] p-4">
          <div className="text-[9px] uppercase tracking-widest text-[#3B4A63] font-bold">Total Tagihan</div>
          <div className="text-lg font-bold text-[#3B4A63] mt-0.5 font-num tabular-nums">{fmtIDR(grandTotal)}</div>
          <div className="text-[10px] text-[#3B4A63] mt-0.5">{payments.length} pembayaran tercatat</div>
        </div>
      </div>

      <div className="h-1.5 rounded-full bg-white overflow-hidden border border-[#DCE0E7]/60">
        <div className="h-full bg-gradient-to-r from-[#57D28A] to-[#3EB876] transition-all" style={{ width: `${pct}%` }} />
      </div>

      {canEdit && !locked && (
        <div className="rounded-2xl overflow-hidden bg-white/60 border border-[#B0C7EA]/60">
          <button type="button" onClick={() => setOpen((v) => !v)} data-testid="invoice-pay-form-toggle" className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/80 transition-colors">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#6B8FE2] to-[#3B4A63] text-white flex items-center justify-center"><Plus className="w-4 h-4" /></div>
              <div className="text-sm font-bold text-[#090808]">Catat Pembayaran</div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-[#6B8FE2] font-semibold hidden sm:inline">Sisa: <span className="font-num tabular-nums">{fmtIDR(outstanding)}</span></span>
              <ChevronDown className={`w-4 h-4 text-[#3B4A63] transition-transform ${open ? "rotate-180" : ""}`} />
            </div>
          </button>
          {open && (
            <div className="px-4 pb-4 pt-1 space-y-3" data-testid="invoice-pay-form-panel">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] font-bold uppercase tracking-widest text-[#3B4A63]">Nominal *</Label>
                  <NumberInput data-testid="invoice-pay-amount" value={pay.amount} onChange={(v) => setPay({ ...pay, amount: v })} className="bg-white rounded-xl h-11 mt-1" />
                </div>
                <div>
                  <Label className="text-[10px] font-bold uppercase tracking-widest text-[#3B4A63]">Tanggal Bayar</Label>
                  <DateInput data-testid="invoice-pay-date" value={pay.paid_at} onChange={(e) => setPay({ ...pay, paid_at: e.target.value })} className="bg-white rounded-xl h-11" wrapperClassName="mt-1" />
                </div>
                <div>
                  <Label className="text-[10px] font-bold uppercase tracking-widest text-[#3B4A63]">Metode</Label>
                  <Input data-testid="invoice-pay-method" value={pay.method} onChange={(e) => setPay({ ...pay, method: e.target.value })} placeholder="Transfer BCA, Tunai…" className="bg-white rounded-xl h-11 mt-1" />
                </div>
                <div>
                  <Label className="text-[10px] font-bold uppercase tracking-widest text-[#3B4A63]">Keterangan *</Label>
                  <Input data-testid="invoice-pay-note" value={pay.note} onChange={(e) => setPay({ ...pay, note: e.target.value })} placeholder="DP, Pelunasan…" className="bg-white rounded-xl h-11 mt-1" />
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx" onChange={(e) => pickFile(e.target.files?.[0])} className="hidden" data-testid="invoice-pay-file-input" />
                <button type="button" onClick={() => fileRef.current?.click()} data-testid="invoice-pay-file-btn" className="h-9 px-4 rounded-full border border-[#B0C7EA] text-[#3B4A63] hover:bg-[#E5EEFA] text-xs font-semibold inline-flex items-center gap-1.5">
                  <Paperclip className="w-3.5 h-3.5" /> {file ? "Ganti Bukti" : "Lampirkan Bukti"}
                </button>
                {file && (
                  <div className="inline-flex items-center gap-1.5 text-[11px] text-[#3B4A63] bg-white rounded-full px-3 h-8 border border-[#B0C7EA]">
                    <FileText className="w-3 h-3" /> {file.name}
                    <button type="button" onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; }} className="text-[#CE6968] ml-1"><X className="w-3 h-3" /></button>
                  </div>
                )}
                <div className="flex-1" />
                <button onClick={submit} disabled={busy} data-testid="invoice-pay-submit" className="h-9 px-5 rounded-full bg-gradient-to-r from-[#6B8FE2] to-[#3B4A63] text-white text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-60">
                  <Plus className="w-3.5 h-3.5" /> {busy ? "Menyimpan…" : "Simpan"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {canEdit && locked && (
        <div className="rounded-2xl bg-[#FFF7E8] border border-[#F2D9A0] p-4 text-[12px] text-[#8C6318]" data-testid="invoice-pay-locked">{lockedReason}</div>
      )}

      <div className="space-y-2">
        {payments.length === 0 ? (
          <div className="rounded-2xl bg-white/50 border border-[#DCE0E7]/60 p-8 text-center text-sm text-[#A0A3A8]" data-testid="invoice-payments-empty">
            Belum ada pembayaran untuk invoice ini.
          </div>
        ) : (
          payments.map((p) => (
            <div key={p.id} data-testid={`invoice-payment-row-${p.id}`} className="rounded-2xl bg-white border border-[#DCE0E7]/60 p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#57D28A] to-[#3EB876] text-white flex items-center justify-center shrink-0">
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-[#090808] truncate">{p.note || "Pembayaran"}{p.method ? ` · ${p.method}` : ""}</div>
                <div className="text-[11px] text-[#A0A3A8] mt-0.5 flex items-center gap-2 flex-wrap">
                  <span className="font-num tabular-nums">{p.paid_at ? fmtDate(p.paid_at) : "-"}</span>
                  {p.attachment && (
                    <button onClick={() => onOpenAttachment?.(p)} data-testid={`invoice-payment-attachment-${p.id}`} className="inline-flex items-center gap-1 text-[#6B8FE2] hover:underline">
                      <Paperclip className="w-3 h-3" /> bukti
                    </button>
                  )}
                </div>
              </div>
              <div className="text-base font-bold text-[#090808] font-num tabular-nums">{fmtIDR(p.amount)}</div>
              {canEdit && (
                <button onClick={() => onDelete?.(p)} data-testid={`invoice-payment-delete-${p.id}`} className="w-8 h-8 rounded-full hover:bg-[#FDE7E9] text-[#CE6968] flex items-center justify-center shrink-0">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
